import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

interface SaleItem {
  product_id: string;
  quantity: number;
  unit_price: number;
  description: string;
}

interface Sale {
  id: string;
  created_at: string;
  customer_name: string | null;
  note: string | null;
  payment_method: "nakit" | "kart" | "iban";
  total_amount: number;
  staff_id: string | null;
  sale_items: SaleItem[];
}

const paymentColors: Record<string, string> = {
  nakit: "bg-emerald-100 text-emerald-700",
  kart:  "bg-blue-100 text-blue-700",
  iban:  "bg-violet-100 text-violet-700",
};
const paymentLabels: Record<string, string> = {
  nakit: "Nakit",
  kart:  "Kredi Kartı",
  iban:  "IBAN / Havale",
};

type DatePreset = "today" | "yesterday" | "week" | "month" | "all";
const presets: { id: DatePreset; label: string }[] = [
  { id: "today",     label: "Bugün"    },
  { id: "yesterday", label: "Dün"      },
  { id: "week",      label: "Son 7 Gün"},
  { id: "month",     label: "Bu Ay"    },
  { id: "all",       label: "Tümü"     },
];

function getRange(preset: DatePreset): { s: string; e: string } {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  if (preset === "today")     return { s: today, e: today };
  if (preset === "yesterday") {
    const d = new Date(now); d.setDate(d.getDate() - 1);
    const ds = d.toISOString().slice(0, 10);
    return { s: ds, e: ds };
  }
  if (preset === "week") {
    const d = new Date(now); d.setDate(d.getDate() - 6);
    return { s: d.toISOString().slice(0, 10), e: today };
  }
  if (preset === "month") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return { s: d.toISOString().slice(0, 10), e: today };
  }
  return { s: "", e: "" };
}

export default function WarehouseSalesReportPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId || user?.id || "";

  const [sales, setSales]       = useState<Sale[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [preset, setPreset]     = useState<DatePreset>("today");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate]     = useState(() => new Date().toISOString().slice(0, 10));
  const [payFilter, setPayFilter] = useState<string>("all");
  const [query, setQuery]         = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      let q = supabase
        .from("warehouse_sales")
        .select("id, created_at, customer_name, note, payment_method, total_amount, staff_id")
        .eq("dealer_id", dealerId)
        .order("created_at", { ascending: false });

      if (startDate) q = q.gte("created_at", `${startDate}T00:00:00`);
      if (endDate)   q = q.lte("created_at", `${endDate}T23:59:59`);

      const { data: salesData, error: salesErr } = await q;
      if (salesErr) throw salesErr;

      const salesWithItems = await Promise.all(
        (salesData || []).map(async (sale: any) => {
          const { data: items } = await supabase
            .from("warehouse_sale_items")
            .select("product_id, quantity, unit_price, description")
            .eq("sale_id", sale.id);
          return { ...sale, sale_items: items || [] };
        })
      );
      setSales(salesWithItems as Sale[]);
    } catch (e: any) {
      setError(e.message || "Veriler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (p: DatePreset) => {
    setPreset(p);
    const range = getRange(p);
    setStartDate(range.s);
    setEndDate(range.e);
  };

  const filtered = useMemo(() => {
    return sales.filter((s) => {
      const matchPay = payFilter === "all" || s.payment_method === payFilter;
      const q = query.trim().toLowerCase();
      const matchQ = !q || (s.customer_name?.toLowerCase().includes(q) ?? false) ||
                           s.id.toLowerCase().includes(q);
      return matchPay && matchQ;
    });
  }, [sales, payFilter, query]);

  const totalRevenue = filtered.reduce((sum, s) => sum + Number(s.total_amount), 0);
  const byMethod = filtered.reduce<Record<string, number>>((acc, s) => {
    acc[s.payment_method] = (acc[s.payment_method] || 0) + Number(s.total_amount);
    return acc;
  }, {});

  const formatDate = (d: string) =>
    new Date(d).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const exportCSV = () => {
    const header = "Tarih,Müşteri,Ödeme,Toplam,Not";
    const rows = filtered.map((s) =>
      [formatDate(s.created_at), s.customer_name || "", paymentLabels[s.payment_method], s.total_amount, s.note || ""]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `depo-satis-raporu-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950">Depo Satış Raporu</h4>
          <p className="text-sm text-foreground-500 mt-0.5">Tüm depo satışlarını görüntüleyin</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer hover:bg-background-200/70">
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i> Yenile
          </button>
          <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium cursor-pointer">
            <i className="ri-download-line"></i> CSV
          </button>
        </div>
      </div>

      {/* Date presets */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {presets.map((p) => (
          <button
            key={p.id}
            onClick={() => applyPreset(p.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
              preset === p.id
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
            }`}
          >
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPreset("all"); }}
            className="px-2 py-1.5 bg-background-50 border border-background-200/70 rounded-lg text-xs focus:outline-none cursor-pointer" />
          <span className="text-foreground-400 text-xs">—</span>
          <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPreset("all"); }}
            className="px-2 py-1.5 bg-background-50 border border-background-200/70 rounded-lg text-xs focus:outline-none cursor-pointer" />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Toplam Satış",   val: `₺${totalRevenue.toFixed(2)}`,  icon: "ri-money-dollar-circle-line", color: "text-foreground-950" },
          { label: "Nakit",          val: `₺${(byMethod.nakit || 0).toFixed(2)}`, icon: "ri-cash-line",         color: "text-emerald-700"   },
          { label: "Kart",           val: `₺${(byMethod.kart  || 0).toFixed(2)}`, icon: "ri-bank-card-line",    color: "text-blue-700"      },
          { label: "IBAN / Havale",  val: `₺${(byMethod.iban  || 0).toFixed(2)}`, icon: "ri-exchange-dollar-line", color: "text-violet-700" },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <i className={`${kpi.icon} text-xl ${kpi.color} block mb-1`}></i>
            <p className={`text-base font-bold ${kpi.color}`}>{kpi.val}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Müşteri adı ara..." className="w-full pl-9 pr-4 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
        </div>
        <div className="flex gap-1">
          {[
            { id: "all",  label: "Tümü" },
            { id: "nakit",label: "Nakit" },
            { id: "kart", label: "Kart"  },
            { id: "iban", label: "IBAN"  },
          ].map((f) => (
            <button key={f.id} onClick={() => setPayFilter(f.id)}
              className={`px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                payFilter === f.id ? "bg-foreground-700 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-accent-50 border border-accent-200 rounded-xl text-sm text-accent-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i> {error}
          <button onClick={load} className="ml-auto underline cursor-pointer">Tekrar Dene</button>
        </div>
      )}

      {/* Sales list */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-20 text-center"><i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <i className="ri-receipt-line text-3xl text-foreground-300 block mb-2"></i>
            <p className="text-sm text-foreground-400">Bu dönem için satış bulunamadı</p>
          </div>
        ) : (
          <div className="divide-y divide-background-200/70">
            {filtered.map((sale) => {
              const isOpen = expandedId === sale.id;
              return (
                <div key={sale.id}>
                  <button
                    onClick={() => setExpandedId(isOpen ? null : sale.id)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-background-100/50 transition-colors cursor-pointer text-left"
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${paymentColors[sale.payment_method]}`}>
                      <i className={
                        sale.payment_method === "nakit" ? "ri-money-dollar-circle-line" :
                        sale.payment_method === "kart"  ? "ri-bank-card-line" :
                        "ri-exchange-dollar-line"
                      }></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground-950 text-sm">{sale.customer_name || "Müşterisiz Satış"}</span>
                        <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${paymentColors[sale.payment_method]}`}>
                          {paymentLabels[sale.payment_method]}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-400">{formatDate(sale.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-bold text-foreground-950">₺{Number(sale.total_amount).toFixed(2)}</span>
                      <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${isOpen ? "rotate-180" : ""}`}></i>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4">
                      <div className="bg-background-100 rounded-xl divide-y divide-background-200/70">
                        {sale.sale_items.map((item, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-2.5 text-sm">
                            <span className="text-foreground-700">{item.quantity}× {item.description}</span>
                            <span className="font-medium text-foreground-950">₺{(item.quantity * item.unit_price).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between px-3 py-2.5 font-bold text-foreground-950">
                          <span>Toplam</span>
                          <span>₺{Number(sale.total_amount).toFixed(2)}</span>
                        </div>
                      </div>
                      {sale.note && (
                        <p className="text-xs text-foreground-400 mt-2 flex items-center gap-1">
                          <i className="ri-file-text-line"></i> {sale.note}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs text-foreground-400 text-right mt-2">{filtered.length} satış · Toplam ₺{totalRevenue.toFixed(2)}</p>
    </div>
  );
}
